import type {
  ApiResponse,
  AuthSession,
  AuthUser,
  CloudinarySignature,
  CreatePetBookingRequest,
  CreatePetBlockedDateRequest,
  PetBlockedDate,
  CreatePetHistoryRequest,
  ForgotPasswordRequest,
  HealthCheckData,
  LoginRequest,
  PaginatedResponse,
  PetAnalysis,
  PetEarnings,
  PetBooking,
  PetHistoryEntry,
  PetRanking,
  UpdatePetRankingRequest,
  CreateReadingBookRequest,
  ReadingBook,
  CreateMovieRequest,
  Movie,
  UpdateMovieRequest,
  RegisterRequest,
  ResetPasswordRequest,
  UpdatePetBookingRequest,
  UpdatePetHistoryRequest,
  UpdateProfileRequest,
  UpdateReadingBookRequest,
  UsageData,
  TravelCity,
  CreateTravelCityRequest,
  UpdateTravelCityRequest,
  TravelCountryGuide,
  CreateTravelCountryGuideRequest,
  UpdateTravelCountryGuideRequest,
  TravelRoute,
  CreateTravelRouteRequest,
  UpdateTravelRouteRequest,
  Dish,
  CreateDishRequest,
  UpdateDishRequest,
  Drink,
  CreateDrinkRequest,
  UpdateDrinkRequest,
  DishRequest,
  CreateDishRequestRequest,
  AccountRequest,
  CreateAccountRequestRequest,
  NotificationSummary,
  Space,
  UpdateSpaceRequest,
  CoupleProfile,
  UpdateCoupleProfileRequest,
  DiaryDate,
  CreateDiaryDateRequest,
  UpdateDiaryDateRequest,
  DiaryMoment,
  CreateDiaryMomentRequest,
  UpdateDiaryMomentRequest,
} from "@al-life/types";
import { getToken } from "./authStore";

/**
 * Typed API client.
 *
 * Wraps fetch with:
 * - Base URL from Vite env variable (VITE_API_URL)
 * - Automatic `Authorization: Bearer <token>` header when logged in
 * - Consistent error handling
 * - TypeScript response types from @al-life/types
 *
 * Usage:
 *   const result = await api.health.check();
 *   if (result.success) console.log(result.data.status);
 */

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

/** Carries the HTTP status alongside the envelope so callers can branch on 401/403. */
export interface ApiResult<T> extends ApiResponse<T> {
  status: number;
}

async function request<T>(
  path: string,
  options?: RequestInit
): Promise<ApiResult<T>> {
  const url = `${BASE_URL}${path}`;
  const token = getToken();

  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    return {
      success: false,
      status: response.status,
      error: (errorBody as ApiResponse).error ?? `HTTP ${response.status}`,
      timestamp: new Date().toISOString(),
    };
  }

  // A 200 with a non-JSON body (e.g. an SPA HTML fallback when VITE_API_URL is
  // unset in dev, or a proxy/CDN error page) must NOT throw — that would reject
  // the loader promise and leave callers stuck on their loading state. Turn it
  // into a clean error result instead.
  try {
    const body = (await response.json()) as ApiResponse<T>;
    return { ...body, status: response.status };
  } catch {
    return {
      success: false,
      status: response.status,
      error: "Unexpected response from the server",
      timestamp: new Date().toISOString(),
    };
  }
}

function jsonBody(data: unknown): RequestInit {
  return { method: "POST", body: JSON.stringify(data) };
}

/** Space-scoped path prefix: `/spaces/<slug>`. */
function s(slug: string): string {
  return `/spaces/${encodeURIComponent(slug)}`;
}

// ─── Namespaced API methods ────────────────────────────────────────────────────

export const api = {
  health: {
    // Returns the standard ApiResponse envelope with HealthCheckData inside
    check: () => request<HealthCheckData>("/health"),
  },

  uploads: {
    // Get a short-lived signature for a direct-to-Cloudinary browser upload
    sign: () => request<CloudinarySignature>("/uploads/sign", { method: "POST" }),
  },

  admin: {
    // Month-to-date usage + cost (admin only)
    usage: () => request<UsageData>("/admin/usage"),
  },

  auth: {
    register: (body: RegisterRequest) =>
      request<{ message: string }>("/auth/register", jsonBody(body)),

    verifyEmail: (token: string) =>
      request<AuthSession>("/auth/verify-email", jsonBody({ token })),

    resendVerification: (email: string) =>
      request<{ message: string }>("/auth/resend-verification", jsonBody({ email })),

    login: (body: LoginRequest) =>
      request<AuthSession>("/auth/login", jsonBody(body)),

    me: () => request<AuthUser>("/auth/me"),

    updateProfile: (body: UpdateProfileRequest) =>
      request<AuthUser>("/auth/me", { method: "PATCH", body: JSON.stringify(body) }),

    forgotPassword: (body: ForgotPasswordRequest) =>
      request<{ message: string }>("/auth/forgot-password", jsonBody(body)),

    resetPassword: (body: ResetPasswordRequest) =>
      request<{ message: string }>("/auth/reset-password", jsonBody(body)),

    // "Request access" — self-signup is disabled while SES is sandboxed, so a
    // visitor requests an account (public) and the admin approves it.
    requestAccess: (body: CreateAccountRequestRequest) =>
      request<{ message: string }>("/auth/requests", jsonBody(body)),

    // Admin-only account-request management.
    requests: {
      list: () => request<AccountRequest[]>("/auth/requests"),
      approve: (id: string) =>
        request<AccountRequest>(`/auth/requests/${id}/approve`, { method: "POST" }),
      dismiss: (id: string) =>
        request<{ message: string }>(`/auth/requests/${id}`, { method: "DELETE" }),
    },
  },

  // Admin message center — one call feeds the unread badge + the panel.
  notifications: {
    summary: () => request<NotificationSummary>("/notifications/summary"),
  },

  // ── Spaces (multi-tenant) ──
  // Every content call below is scoped to a space `slug`. `s(slug)` builds the
  // shared prefix so the paths read cleanly.
  spaces: {
    get: (slug: string) => request<Space>(`/spaces/${encodeURIComponent(slug)}`),
    mine: () => request<Space[]>("/spaces/mine"),
    update: (slug: string, body: UpdateSpaceRequest) =>
      request<Space>(`/spaces/${encodeURIComponent(slug)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
  },

  pets: {
    // ── Shared calendar bookings ──
    bookings: {
      // Load a whole month at once: month is "YYYY-MM"
      list: (slug: string, month: string) =>
        request<PetBooking[]>(`${s(slug)}/pets/bookings?month=${encodeURIComponent(month)}`),

      create: (slug: string, body: CreatePetBookingRequest) =>
        request<PetBooking>(`${s(slug)}/pets/bookings`, jsonBody(body)),

      update: (slug: string, id: string, body: UpdatePetBookingRequest) =>
        request<PetBooking>(`${s(slug)}/pets/bookings/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        }),

      remove: (slug: string, id: string) =>
        request<{ message: string }>(`${s(slug)}/pets/bookings/${id}`, { method: "DELETE" }),
    },

    // ── Blocked dates (days unavailable for new bookings) ──
    blocked: {
      list: (slug: string, month: string) =>
        request<PetBlockedDate[]>(`${s(slug)}/pets/blocked?month=${encodeURIComponent(month)}`),

      create: (slug: string, body: CreatePetBlockedDateRequest) =>
        request<PetBlockedDate>(`${s(slug)}/pets/blocked`, jsonBody(body)),

      remove: (slug: string, id: string) =>
        request<{ message: string }>(`${s(slug)}/pets/blocked/${id}`, { method: "DELETE" }),
    },

    // ── History photo wall ──
    history: {
      list: (slug: string, page = 1) =>
        request<PaginatedResponse<PetHistoryEntry>>(`${s(slug)}/pets/history?page=${page}`),

      create: (slug: string, body: CreatePetHistoryRequest) =>
        request<PetHistoryEntry>(`${s(slug)}/pets/history`, jsonBody(body)),

      update: (slug: string, id: string, body: UpdatePetHistoryRequest) =>
        request<PetHistoryEntry>(`${s(slug)}/pets/history/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        }),

      remove: (slug: string, id: string) =>
        request<{ message: string }>(`${s(slug)}/pets/history/${id}`, { method: "DELETE" }),
    },

    // ── Insights dashboard (aggregated all-time) ──
    analysis: (slug: string) => request<PetAnalysis>(`${s(slug)}/pets/analysis`),

    // ── Earnings dashboard (members only — money is never public) ──
    earnings: (slug: string) => request<PetEarnings>(`${s(slug)}/pets/earnings`),

    // ── Private dog ranking (members only) ──
    ranking: {
      get: (slug: string) => request<PetRanking>(`${s(slug)}/pets/ranking`),

      update: (slug: string, body: UpdatePetRankingRequest) =>
        request<PetRanking>(`${s(slug)}/pets/ranking`, {
          method: "PUT",
          body: JSON.stringify(body),
        }),
    },
  },

  reading: {
    list: (slug: string) => request<ReadingBook[]>(`${s(slug)}/reading/books`),

    create: (slug: string, body: CreateReadingBookRequest) =>
      request<ReadingBook>(`${s(slug)}/reading/books`, jsonBody(body)),

    update: (slug: string, id: string, body: UpdateReadingBookRequest) =>
      request<ReadingBook>(`${s(slug)}/reading/books/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),

    remove: (slug: string, id: string) =>
      request<{ message: string }>(`${s(slug)}/reading/books/${id}`, { method: "DELETE" }),
  },

  // Movies live under the same "reading" section (one room, two shelves).
  movies: {
    list: (slug: string) => request<Movie[]>(`${s(slug)}/reading/movies`),

    create: (slug: string, body: CreateMovieRequest) =>
      request<Movie>(`${s(slug)}/reading/movies`, jsonBody(body)),

    update: (slug: string, id: string, body: UpdateMovieRequest) =>
      request<Movie>(`${s(slug)}/reading/movies/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),

    remove: (slug: string, id: string) =>
      request<{ message: string }>(`${s(slug)}/reading/movies/${id}`, { method: "DELETE" }),
  },

  travel: {
    // ── Visited cities (pins) ──
    cities: {
      list: (slug: string) => request<TravelCity[]>(`${s(slug)}/travel/cities`),

      create: (slug: string, body: CreateTravelCityRequest) =>
        request<TravelCity>(`${s(slug)}/travel/cities`, jsonBody(body)),

      update: (slug: string, id: string, body: UpdateTravelCityRequest) =>
        request<TravelCity>(`${s(slug)}/travel/cities/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        }),

      remove: (slug: string, id: string) =>
        request<{ message: string }>(`${s(slug)}/travel/cities/${id}`, { method: "DELETE" }),
    },

    // ── Country-level guides (攻略) ──
    guides: {
      list: (slug: string) => request<TravelCountryGuide[]>(`${s(slug)}/travel/country-guides`),

      create: (slug: string, body: CreateTravelCountryGuideRequest) =>
        request<TravelCountryGuide>(`${s(slug)}/travel/country-guides`, jsonBody(body)),

      update: (slug: string, id: string, body: UpdateTravelCountryGuideRequest) =>
        request<TravelCountryGuide>(`${s(slug)}/travel/country-guides/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        }),

      remove: (slug: string, id: string) =>
        request<{ message: string }>(`${s(slug)}/travel/country-guides/${id}`, { method: "DELETE" }),
    },

    // ── Routes (出行足迹 — flight/train/car trips) ──
    routes: {
      list: (slug: string) => request<TravelRoute[]>(`${s(slug)}/travel/routes`),

      create: (slug: string, body: CreateTravelRouteRequest) =>
        request<TravelRoute>(`${s(slug)}/travel/routes`, jsonBody(body)),

      update: (slug: string, id: string, body: UpdateTravelRouteRequest) =>
        request<TravelRoute>(`${s(slug)}/travel/routes/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        }),

      remove: (slug: string, id: string) =>
        request<{ message: string }>(`${s(slug)}/travel/routes/${id}`, { method: "DELETE" }),
    },
  },

  cooking: {
    // ── Menu dishes (recipe steps attached only for space members) ──
    dishes: {
      list: (slug: string) => request<Dish[]>(`${s(slug)}/cooking/dishes`),

      get: (slug: string, id: string) => request<Dish>(`${s(slug)}/cooking/dishes/${id}`),

      create: (slug: string, body: CreateDishRequest) =>
        request<Dish>(`${s(slug)}/cooking/dishes`, jsonBody(body)),

      update: (slug: string, id: string, body: UpdateDishRequest) =>
        request<Dish>(`${s(slug)}/cooking/dishes/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        }),

      remove: (slug: string, id: string) =>
        request<{ message: string }>(`${s(slug)}/cooking/dishes/${id}`, { method: "DELETE" }),
    },

    // ── Drinks 酒水单 (mixing steps attached only for space members) ──
    drinks: {
      list: (slug: string) => request<Drink[]>(`${s(slug)}/cooking/drinks`),

      get: (slug: string, id: string) => request<Drink>(`${s(slug)}/cooking/drinks/${id}`),

      create: (slug: string, body: CreateDrinkRequest) =>
        request<Drink>(`${s(slug)}/cooking/drinks`, jsonBody(body)),

      update: (slug: string, id: string, body: UpdateDrinkRequest) =>
        request<Drink>(`${s(slug)}/cooking/drinks/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        }),

      remove: (slug: string, id: string) =>
        request<{ message: string }>(`${s(slug)}/cooking/drinks/${id}`, { method: "DELETE" }),
    },

    // ── Requests ("orders") ──
    requests: {
      // Space-member inbox — all requests
      list: (slug: string) => request<DishRequest[]>(`${s(slug)}/cooking/requests`),

      // The logged-in caller's own requests
      mine: (slug: string) => request<DishRequest[]>(`${s(slug)}/cooking/requests/mine`),

      create: (slug: string, body: CreateDishRequestRequest) =>
        request<DishRequest>(`${s(slug)}/cooking/requests`, jsonBody(body)),

      // Mark done / un-done (member) — unhandled requests are the "unread" count.
      setHandled: (slug: string, id: string, handled: boolean) =>
        request<DishRequest>(`${s(slug)}/cooking/requests/${id}`, {
          method: "PATCH",
          body: JSON.stringify({ handled }),
        }),

      remove: (slug: string, id: string) =>
        request<{ message: string }>(`${s(slug)}/cooking/requests/${id}`, { method: "DELETE" }),
    },
  },

  diary: {
    // ── Couple profile (Overview hero); GET may return null until set up ──
    profile: {
      get: (slug: string) => request<CoupleProfile | null>(`${s(slug)}/diary/profile`),

      save: (slug: string, body: UpdateCoupleProfileRequest) =>
        request<CoupleProfile>(`${s(slug)}/diary/profile`, {
          method: "PUT",
          body: JSON.stringify(body),
        }),
    },

    // ── Important dates ──
    dates: {
      list: (slug: string) => request<DiaryDate[]>(`${s(slug)}/diary/dates`),

      create: (slug: string, body: CreateDiaryDateRequest) =>
        request<DiaryDate>(`${s(slug)}/diary/dates`, jsonBody(body)),

      update: (slug: string, id: string, body: UpdateDiaryDateRequest) =>
        request<DiaryDate>(`${s(slug)}/diary/dates/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        }),

      remove: (slug: string, id: string) =>
        request<{ message: string }>(`${s(slug)}/diary/dates/${id}`, { method: "DELETE" }),
    },

    // ── Moments (travel / firstTime / remarkable grids) ──
    moments: {
      list: (slug: string) => request<DiaryMoment[]>(`${s(slug)}/diary/moments`),

      create: (slug: string, body: CreateDiaryMomentRequest) =>
        request<DiaryMoment>(`${s(slug)}/diary/moments`, jsonBody(body)),

      update: (slug: string, id: string, body: UpdateDiaryMomentRequest) =>
        request<DiaryMoment>(`${s(slug)}/diary/moments/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        }),

      remove: (slug: string, id: string) =>
        request<{ message: string }>(`${s(slug)}/diary/moments/${id}`, { method: "DELETE" }),
    },
  },
};
